import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Search,
  Clipboard,
  Copy,
  CopyPlus,
  Trash2,
  PlayCircle,
  Group,
  Ungroup,
  GitBranch,
  RotateCcw,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useReactFlow } from '@xyflow/react';
import {
  useNodeStore,
  loadClipboardFromStorage,
  selectSelectedNodeIds,
} from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { NodeStruct, NodeType } from '@/types/node-types';
import { getIconComponent } from '@/pages/workflow/helper/icon-mapping';
import { getNodeIconColor } from '@/constants/node-styling';
import { isPickerHiddenNode } from '@/constants/node-catalog';
import { NodeItem } from './node-list';
import { canUngroupFromContext } from '@/pages/workflow/helper/group-nodes';
import {
  findJudgmentGateStructs,
  stampJudgmentGate,
} from '@/pages/workflow/helper/judgment-gate-stamp';
// --- Phase 5: one-click patterns on the selected node ---
import {
  addDecisionGateAfter,
  decisionGateAvailability,
  makeRepeatUntilPasses,
  repeatAvailability,
} from '@/pages/workflow/helper/pattern-stamps';
import {
  LOGIC_TEMPLATES,
  insertTemplate,
  templateAvailability,
} from '@/pages/workflow/helper/logic-templates';
import { Sparkles } from 'lucide-react';
// --- end Phase 5 ---

interface CanvasContextMenuProps {
  children: React.ReactNode;
  onNodeAdd: (
    nodeStruct: NodeStruct,
    position?: { x: number; y: number },
  ) => void;
  onPaste: (position?: { x: number; y: number }) => void;
  isLocked?: boolean;
  /**
   * "Re-run from this node" handler.  Relevant in two contexts:
   *
   *   * Historical view -- start a brand-new run seeded from the
   *     past run's cached results.
   *   * Paused live workflow -- send a resume signal that
   *     invalidates the chosen node + downstream and resumes
   *     from there (uses the same edit-during-pause primitive).
   *
   * The editor passes ``undefined`` outside both contexts so the
   * menu item doesn't render.
   */
  onRerunFromNode?: (nodeId: string) => Promise<void> | void;
  /**
   * Whether the editor is currently displaying a past run OR a
   * live paused workflow -- either context makes "Re-run from
   * this node" meaningful.  Combined into a single flag here so
   * the menu doesn't have to know about pause / historical
   * distinctly; the editor decides and toggles this on for both.
   */
  isHistoricalData?: boolean;
  /**
   * Group the current multi-selection so nodes move together (e.g. a step
   * plus its sticky-note annotation).
   */
  onGroupNodes?: () => void;
  /** Ungroup nodes from the right-click target or current selection. */
  onUngroupNodes?: (contextNodeId?: string | null) => void;
  canGroupSelection?: boolean;
  groupEligibleCount?: number;
}

export const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
  children,
  onNodeAdd,
  onPaste,
  isLocked = false,
  onRerunFromNode,
  isHistoricalData = false,
  onGroupNodes,
  onUngroupNodes,
  canGroupSelection = false,
  groupEligibleCount = 0,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [clickPosition, setClickPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isRightClickOnSelectionBox, setIsRightClickOnSelectionBox] =
    useState(false);
  const [hadSelectedNodesWhenOpened, setHadSelectedNodesWhenOpened] =
    useState(false);
  const [clipboardNodeCount, setClipboardNodeCount] = useState(0);
  const [clickedNodeIdOnRightClick, setClickedNodeIdOnRightClick] = useState<
    string | null
  >(null);
  // Snapshots of the menu's context, taken once at open time. Multi-select
  // (right-click within a drag-selection box) has no single clickedNodeId to
  // key off of, so showStandardNodeActions/showUngroupAction/the count text
  // would otherwise read the live, mutable selectedNodeIds/nodes — which the
  // deferred deselect-on-close (or the action itself, e.g. Delete) can zero
  // out while Radix is still playing the close animation, flashing the menu
  // to a different variant right before it disappears.
  const [selectedCountAtOpen, setSelectedCountAtOpen] = useState(0);
  const [canUngroupAtOpen, setCanUngroupAtOpen] = useState(false);
  // canGroupSelection/groupEligibleCount are the editor's live, store-derived
  // props (recomputed on every nodes change) — same instability risk as
  // above, so they also need an open-time snapshot for the "Group nodes" row.
  const [canGroupAtOpen, setCanGroupAtOpen] = useState(false);
  const [groupEligibleCountAtOpen, setGroupEligibleCountAtOpen] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const closingFromActionItemRef = useRef(false);

  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const {
    groupedNodes,
    recentlyUsedNodes,
    addToRecentlyUsed,
    selectNode: storeSelectNode,
    deselectAllNodes,
    nodes,
    onCopyNode,
    onDuplicateNode,
    onNodesEdgesDelete,
  } = useNodeStore();
  const setGroupContainerFocusedId = useNodeStore(
    (state) => state.setGroupContainerFocusedId,
  );
  // Rendered inside the editor's ReactFlowProvider, so screen→flow
  // conversion is available directly (the editor does the same for onNodeAdd).
  const { screenToFlowPosition } = useReactFlow();

  // Judgment-gate pattern stamp availability: requires classify/validate/
  // switch to all be present in this org's catalog (classify is
  // feature-flagged and may be absent).
  const judgmentGateStructs = useMemo(
    () => findJudgmentGateStructs(groupedNodes),
    [groupedNodes],
  );

  // Flatten all nodes for search
  const allNodes = useMemo(() => {
    const nodes: Array<{
      name: string;
      nodeStruct: NodeStruct;
      category: string;
    }> = [];
    Object.entries(groupedNodes).forEach(([category, nodeList]) => {
      nodeList.forEach((node) => {
        if (isPickerHiddenNode(node)) return;
        nodes.push({
          name: node.display_name,
          nodeStruct: node,
          category,
        });
      });
    });
    return nodes;
  }, [groupedNodes]);

  // Filter nodes based on search query. Matches the tool display name OR the
  // group/server name (tool_category) so e.g. "eBay Seller" surfaces that
  // server's tools even when no individual tool name contains the query.
  // Name matches rank above category-only matches.
  const filteredNodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];

    const nameMatches: typeof allNodes = [];
    const categoryMatches: typeof allNodes = [];
    allNodes.forEach((node) => {
      if (node.name.toLowerCase().includes(query)) {
        nameMatches.push(node);
      } else if (node.nodeStruct.tool_category?.toLowerCase().includes(query)) {
        categoryMatches.push(node);
      }
    });
    return [...nameMatches, ...categoryMatches];
  }, [allNodes, searchQuery]);

  // Get recently used nodes (limit to 5)
  const recentNodes = useMemo(() => {
    return [...recentlyUsedNodes]
      .filter((n) => !isPickerHiddenNode(n.nodeStruct))
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, 5);
  }, [recentlyUsedNodes]);

  // Get suggested nodes to fill remaining slots (up to 5 total)
  const suggestedNodes = useMemo(() => {
    // Define commonly used node names to suggest
    const commonNodeNames = [
      'agent',
      'text input',
      'markdown output',
      'serpapi search',
      'text output',
    ];

    const suggestions: Array<{
      name: string;
      nodeStruct: NodeStruct;
      category: string;
      icon: React.ReactNode;
    }> = [];

    // Calculate how many suggestions we need to fill up to 5 total
    const remainingSlots = Math.max(0, 5 - recentNodes.length);

    if (remainingSlots === 0) return suggestions;

    // Search for common nodes across all categories
    for (const category of Object.keys(groupedNodes)) {
      const categoryNodes = groupedNodes[category];
      if (categoryNodes) {
        for (const node of categoryNodes) {
          if (isPickerHiddenNode(node)) continue;
          const nodeName = node.display_name.toLowerCase();
          if (
            commonNodeNames.some((commonName) =>
              nodeName.includes(commonName.toLowerCase()),
            )
          ) {
            // Check if this node is already in recent nodes
            const isAlreadyRecent = recentNodes.some(
              (recentNode) => recentNode.name === node.display_name,
            );

            if (!isAlreadyRecent) {
              suggestions.push({
                name: node.display_name,
                nodeStruct: node,
                category,
                icon: getIconComponent(
                  node.icon,
                  getNodeIconColor(node.node_type, node.icon_color) ||
                    undefined,
                ),
              });

              // Stop when we have enough suggestions
              if (suggestions.length >= remainingSlots) break;
            }
          }
        }
      }
      if (suggestions.length >= remainingSlots) break;
    }

    return suggestions;
  }, [recentNodes, groupedNodes]);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Build a flat ordered list of all actionable items for keyboard navigation.
  // When searching: search results. Otherwise: recent + suggested nodes.
  const actionableItems = useMemo(() => {
    const items: Array<{ type: 'node'; nodeStruct: NodeStruct }> = [];

    if (searchQuery) {
      filteredNodes.slice(0, 10).forEach((node) => {
        items.push({ type: 'node', nodeStruct: node.nodeStruct });
      });
    } else {
      recentNodes.forEach((node) => {
        items.push({ type: 'node', nodeStruct: node.nodeStruct });
      });
      suggestedNodes.forEach((node) => {
        items.push({ type: 'node', nodeStruct: node.nodeStruct });
      });
    }
    return items;
  }, [searchQuery, filteredNodes, recentNodes, suggestedNodes]);

  // Reset highlight when search query or item list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery, actionableItems.length]);

  const handleNodeClick = useCallback(
    (nodeStruct: NodeStruct) => {
      if (isLocked) return;
      onNodeAdd(nodeStruct, clickPosition || undefined);
      addToRecentlyUsed({
        name: nodeStruct.display_name,
        nodeStruct,
        icon: getIconComponent(
          nodeStruct.icon,
          getNodeIconColor(nodeStruct.node_type, nodeStruct.icon_color) ||
            undefined,
        ),
        lastUsed: Date.now(),
        category: nodeStruct.node_type || 'Unknown',
      });
    },
    [onNodeAdd, addToRecentlyUsed, clickPosition, isLocked],
  );

  const handleContextMenuOpen = useCallback(
    (event: React.MouseEvent) => {
      // Reset the previous session's search state here (on open) rather
      // than on close — clearing it on close mutates state Radix's exit
      // animation is still rendering with, which flashes the closing menu
      // back to its default content right before it disappears.
      setSearchQuery('');
      setHighlightedIndex(-1);
      // Same reasoning: snapshot the editor's live canGroupSelection/
      // groupEligibleCount props so the "Group nodes" row doesn't
      // appear/disappear or change its count while closing.
      setCanGroupAtOpen(canGroupSelection);
      setGroupEligibleCountAtOpen(groupEligibleCount);

      // Check the event.target to see what was clicked
      const target = event.target as HTMLElement;

      // The multi-selection bounding box is `pointer-events: none` (see
      // generic-node/style.css) so left-clicks reach selected nodes, which
      // means it never shows up as the event target. Measure its client rect
      // instead so right-click inside the selection still gets the multi-
      // select menu — same behavior as when the box was interactive.
      const selectionBox = document
        .querySelector('.react-flow__nodesselection-rect')
        ?.getBoundingClientRect();
      const isOnSelectionBox =
        target.closest('.react-flow__nodesselection-rect') !== null ||
        (!!selectionBox &&
          event.clientX >= selectionBox.left &&
          event.clientX <= selectionBox.right &&
          event.clientY >= selectionBox.top &&
          event.clientY <= selectionBox.bottom);

      // Check if right-click is on a node element
      const nodeElement = target.closest('.react-flow__node');
      let clickedNodeId: string | null = null;

      if (nodeElement) {
        // React Flow stores node ID in data-id attribute
        // Try data-id first, then fallback to id attribute
        clickedNodeId =
          nodeElement.getAttribute('data-id') ||
          nodeElement.getAttribute('id') ||
          null;

        // If still not found, try to extract from the element's class or other attributes
        // React Flow sometimes uses the id directly as an attribute
        if (!clickedNodeId && nodeElement.id) {
          clickedNodeId = nodeElement.id;
        }
      }

      // If right-click is on a node (not on selection box), select it first
      if (clickedNodeId && !isOnSelectionBox) {
        const clickedNode = nodes.find((node) => node.id === clickedNodeId);

        // Track the clicked node ID for immediate use in hasSelectedNodes calculation
        setClickedNodeIdOnRightClick(clickedNodeId);

        // If the clicked node is already part of a multi-selection, leave
        // the whole selection intact so actions apply to all of it.
        const wasAlreadySelected = selectedNodeIds.includes(clickedNodeId);
        if (
          clickedNode &&
          clickedNode.type !== NodeType.GroupNode &&
          !wasAlreadySelected
        ) {
          storeSelectNode(clickedNodeId);
        }

        // Mark that we're right-clicking on a selection (either newly selected or already selected)
        if (clickedNode) {
          const isClickedGroup = clickedNode.type === NodeType.GroupNode;
          if (isClickedGroup) {
            setGroupContainerFocusedId(clickedNodeId);
          } else {
            setGroupContainerFocusedId(null);
          }
          setIsRightClickOnSelectionBox(true);
          setHadSelectedNodesWhenOpened(true);
          // If it was already selected as part of a larger selection, the
          // count/actions should reflect that whole selection, not just
          // this one node — otherwise Copy/Duplicate/Delete labels say "1
          // node" while the action runs against every selected node. Group
          // containers are never themselves selectable, so storeSelectNode
          // above leaves whatever selection existed untouched — reflect
          // that retained selection rather than hardcoding 1.
          setSelectedCountAtOpen(
            isClickedGroup || wasAlreadySelected ? selectedNodeIds.length : 1,
          );
          setCanUngroupAtOpen(canUngroupFromContext(nodes, clickedNodeId));
        }
      } else {
        setIsRightClickOnSelectionBox(isOnSelectionBox);
        // Track if we had selected nodes when the menu opened (and was on selection box)
        setHadSelectedNodesWhenOpened(
          isOnSelectionBox && selectedNodeIds.length > 0,
        );
        setSelectedCountAtOpen(isOnSelectionBox ? selectedNodeIds.length : 0);
        setCanUngroupAtOpen(
          isOnSelectionBox ? canUngroupFromContext(nodes, null) : false,
        );
        // Clear clicked node ID if not clicking on a node
        setClickedNodeIdOnRightClick(null);
      }

      // If right-click is on canvas (not on selection box and not on node) and there are selected nodes,
      // deselect them to remove the selection box
      if (!isOnSelectionBox && !clickedNodeId && selectedNodeIds.length > 0) {
        deselectAllNodes();
      }

      setClickPosition({ x: event.clientX, y: event.clientY });
    },
    [
      selectedNodeIds,
      storeSelectNode,
      deselectAllNodes,
      nodes,
      setGroupContainerFocusedId,
      canGroupSelection,
      groupEligibleCount,
    ],
  );

  // Handle context menu open/close state
  const handleContextMenuOpenChange = useCallback(
    (open: boolean) => {
      setIsMenuOpen(open);

      // Load clipboard count from localStorage when menu opens (for cross-tab clipboard support)
      if (open) {
        const { nodes } = loadClipboardFromStorage();
        setClipboardNodeCount(nodes.length);
      }

      // Edge case: When menu closes and we had selected nodes when it opened (right-click on selection box),
      // deselect the nodes if they're still selected (user clicked outside on canvas).
      // Skip when closing from an action item (Copy/Duplicate/Delete/Paste) so we don't deselect new nodes.
      if (
        !open &&
        !closingFromActionItemRef.current &&
        hadSelectedNodesWhenOpened &&
        selectedNodeIds.length > 0
      ) {
        setTimeout(() => {
          const currentSelectedIds = selectSelectedNodeIds(
            useNodeStore.getState(),
          );
          if (currentSelectedIds.length > 0) {
            useNodeStore.getState().deselectAllNodes();
          }
        }, 0);
      }

      if (!open) {
        closingFromActionItemRef.current = false;
        // Deliberately NOT clearing hadSelectedNodesWhenOpened,
        // clickedNodeIdOnRightClick, selectedCountAtOpen, or canUngroupAtOpen
        // here: together they drive hasSelectedNodes/showStandardNodeActions/
        // showUngroupAction/the count text, which
        // decide what menu content renders. Radix keeps ContextMenuContent
        // mounted during its close animation, so clearing (or live-reading)
        // this state now would swap or empty the still-visible (fading out)
        // menu right before it disappears — either from the deferred
        // deselect above, or from the action itself (e.g. Delete) mutating
        // selectedNodeIds/nodes out from under it. The next
        // handleContextMenuOpen call always sets everything fresh, so
        // leaving stale values here is harmless.
      }
    },
    [hadSelectedNodesWhenOpened, selectedNodeIds.length],
  );

  const closeMenu = useCallback(() => {
    closingFromActionItemRef.current = true;
    handleContextMenuOpenChange(false);
  }, [handleContextMenuOpenChange]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (actionableItems.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < actionableItems.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : actionableItems.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < actionableItems.length
          ) {
            handleNodeClick(actionableItems[highlightedIndex].nodeStruct);
            // Radix ContextMenu ignores controlled `open={false}` when focus
            // remains inside the menu content (e.g. the search input). Calling
            // closeMenu() alone doesn't work. Dispatching a native Escape
            // event triggers Radix's built-in dismiss logic reliably.
            menuContentRef.current?.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'Escape',
                bubbles: true,
              }),
            );
          }
          break;
      }
    },
    [actionableItems, highlightedIndex, handleNodeClick],
  );

  const handlePaste = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPaste(clickPosition || undefined);
      closeMenu();
    },
    [onPaste, clickPosition, closeMenu],
  );

  // One-click judgment-gate pattern: stamp Classify → Validate → Switch
  // pre-wired (labels synced into Switch cases + Validate schema) at the
  // right-click position.
  const handleStampJudgmentGate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isLocked || !judgmentGateStructs) return;
      const flowPosition = screenToFlowPosition(
        clickPosition ?? {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        },
      );
      // --- if/else (Phase 8b): a two-label classifier gets an If / Else ---
      stampJudgmentGate(judgmentGateStructs, flowPosition, {
        useIfForTwoLabels: true,
      });
      closeMenu();
    },
    [
      isLocked,
      judgmentGateStructs,
      screenToFlowPosition,
      clickPosition,
      closeMenu,
    ],
  );

  // --- Phase 5: patterns on the one selected node --------------------------
  const singleSelectedNode = useMemo(() => {
    const id =
      selectedNodeIds.length === 1
        ? selectedNodeIds[0]
        : (clickedNodeIdOnRightClick ?? null);
    return id ? nodes.find((node) => node.id === id) : undefined;
  }, [selectedNodeIds, clickedNodeIdOnRightClick, nodes]);
  const decisionGate = useMemo(
    () => decisionGateAvailability(groupedNodes, singleSelectedNode),
    [groupedNodes, singleSelectedNode],
  );
  const repeatPattern = useMemo(
    () => repeatAvailability(groupedNodes, singleSelectedNode),
    [groupedNodes, singleSelectedNode],
  );
  const handleAddDecisionGate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isLocked || !singleSelectedNode) return;
      addDecisionGateAfter(groupedNodes, singleSelectedNode.id);
      closeMenu();
    },
    [isLocked, singleSelectedNode, groupedNodes, closeMenu],
  );
  const handleMakeRepeat = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isLocked || !singleSelectedNode) return;
      makeRepeatUntilPasses(groupedNodes, singleSelectedNode.id);
      closeMenu();
    },
    [isLocked, singleSelectedNode, groupedNodes, closeMenu],
  );
  const handleInsertTemplate = useCallback(
    (templateId: string) => {
      if (isLocked) return;
      const template = LOGIC_TEMPLATES.find((entry) => entry.id === templateId);
      if (!template) return;
      const flowPosition = screenToFlowPosition(
        clickPosition ?? {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        },
      );
      insertTemplate(template, groupedNodes, flowPosition);
      closeMenu();
    },
    [isLocked, groupedNodes, screenToFlowPosition, clickPosition, closeMenu],
  );
  // --- end Phase 5 -----------------------------------------------------------

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCopyNode();
      closeMenu();
    },
    [onCopyNode, closeMenu],
  );

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDuplicateNode();
      closeMenu();
    },
    [onDuplicateNode, closeMenu],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNodesEdgesDelete();
      closeMenu();
    },
    [onNodesEdgesDelete, closeMenu],
  );

  const handleGroup = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onGroupNodes?.();
      closeMenu();
    },
    [onGroupNodes, closeMenu],
  );

  const handleUngroup = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onUngroupNodes?.(clickedNodeIdOnRightClick);
      closeMenu();
    },
    [onUngroupNodes, clickedNodeIdOnRightClick, closeMenu],
  );

  // Check if there are selected nodes and if right-click was on selection box.
  // hadSelectedNodesWhenOpened is a sticky snapshot from open time — it (and
  // selectedCountAtOpen/canUngroupAtOpen below) must NOT fall back to reading
  // the live selectedNodeIds/nodes, since those can be mutated (deselected,
  // deleted) while this menu is still fading out.
  const hasSelectedNodes =
    (selectedNodeIds.length > 0 ||
      clickedNodeIdOnRightClick !== null ||
      hadSelectedNodesWhenOpened) &&
    isRightClickOnSelectionBox;

  // Group containers are never themselves selectable/copyable — Copy/
  // Duplicate/Delete/Group apply to whatever real nodes are still selected,
  // which selectedCountAtOpen already reflects (0 when a bare group
  // container was right-clicked with nothing else selected).
  const showStandardNodeActions = hasSelectedNodes && selectedCountAtOpen > 0;
  const showUngroupAction =
    hasSelectedNodes && canUngroupAtOpen && Boolean(onUngroupNodes);

  // Maintain focus on search input when menu is open and results update
  useEffect(() => {
    if (isMenuOpen && searchInputRef.current && !hasSelectedNodes) {
      // Re-focus if input lost focus (e.g., due to filteredNodes re-render)
      const timeoutId = setTimeout(() => {
        if (document.activeElement !== searchInputRef.current) {
          searchInputRef.current?.focus();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isMenuOpen, hasSelectedNodes, filteredNodes.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && menuContentRef.current) {
      const el = menuContentRef.current.querySelector(
        `[data-action-index="${highlightedIndex}"]`,
      );
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Reusable props to prevent menu items from stealing focus from search input
  const keepInputFocusProps = {
    onPointerMove: (e: React.PointerEvent) => {
      e.preventDefault();
      setHighlightedIndex(-1);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      e.preventDefault();
    },
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
    },
    onFocus: (e: React.FocusEvent) => {
      e.preventDefault();
      searchInputRef.current?.focus();
    },
  };

  return (
    <ContextMenu
      {...({
        open: isMenuOpen,
        onOpenChange: handleContextMenuOpenChange,
      } as React.ComponentProps<typeof ContextMenu>)}
    >
      <ContextMenuTrigger asChild onContextMenu={handleContextMenuOpen}>
        <div className='h-full w-full'>{children}</div>
      </ContextMenuTrigger>
      <ContextMenuContent
        className='w-[350px] rounded-[8px] p-0 max-h-[300px] flex flex-col'
        onPointerLeave={() => {
          if (!hasSelectedNodes) {
            searchInputRef.current?.focus();
          }
        }}
      >
        {/* Search section - always visible at top */}
        {!hasSelectedNodes && (
          <div className='shrink-0'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
              <input
                ref={searchInputRef}
                placeholder='Search nodes'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                className='flex h-9 w-full rounded-none border-b border-gray-300 bg-transparent px-3 py-1 pl-10 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-0 focus:border-gray-300'
              />
            </div>
          </div>
        )}

        {/* Search Results Header - only show when searching */}
        {searchQuery && (
          <div className='shrink-0 px-3 py-2 border-b border-gray-200'>
            <p className='text-sm font-medium text-muted-foreground'>
              {filteredNodes.length > 0 ? 'Search results' : 'No nodes found'}
            </p>
          </div>
        )}

        {/* Scrollable content area */}
        <div ref={menuContentRef} className='flex-1 overflow-y-auto'>
          {/* Selected Nodes Actions - only show when nodes are selected */}
          {hasSelectedNodes && (
            <>
              {/*
                "Re-run from this node" -- the headline action when
                viewing a past run AND a single node is right-clicked.
                Lives at the top of the menu (above Copy/Duplicate)
                because it's the most consequential action available
                in historical view: clicking it starts a brand-new run
                that re-executes from the chosen node onwards using
                cached results for everything upstream.

                Hidden unless:
                  - the editor is currently displaying a past run,
                  - the editor wired a handler in (Temporal-backed
                    workflows only),
                  - exactly one node is selected (the "from" point
                    must be unambiguous).
              */}
              {showStandardNodeActions &&
                isHistoricalData &&
                onRerunFromNode &&
                selectedCountAtOpen === 1 && (
                  <>
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        const targetId =
                          selectedNodeIds[0] ??
                          clickedNodeIdOnRightClick ??
                          null;
                        if (!targetId) return;
                        // Fire-and-forget; the editor surfaces
                        // failures via toast.  We don't await here
                        // because the menu should close immediately.
                        Promise.resolve(onRerunFromNode(targetId)).catch(
                          (err) => {
                            console.error(
                              '[CanvasContextMenu] onRerunFromNode failed:',
                              err,
                            );
                            Sentry.captureException(err, {
                              extra: { context: 'canvas-context-menu-rerun' },
                            });
                          },
                        );
                        closeMenu();
                      }}
                      className='flex items-center gap-3 px-3 py-2 cursor-pointer text-blue-600 focus:text-blue-700 focus:bg-blue-50 dark:focus:bg-blue-950'
                    >
                      <PlayCircle className='size-4 text-blue-600' />
                      <span className='text-sm font-medium'>
                        Re-run from this node
                      </span>
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                  </>
                )}

              {/* --- Phase 5: patterns on the one selected node --- */}
              {showStandardNodeActions &&
                selectedCountAtOpen === 1 &&
                singleSelectedNode &&
                (decisionGate.available || repeatPattern.available) && (
                  <>
                    {decisionGate.available && (
                      <ContextMenuItem
                        onClick={isLocked ? undefined : handleAddDecisionGate}
                        disabled={isLocked}
                        data-testid='pattern-add-decision-gate'
                        className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                      >
                        <GitBranch className='w-4 h-4 text-foreground' />
                        <div className='flex flex-col'>
                          <span className='text-sm'>Add a decision gate</span>
                          <span className='text-xs text-muted-foreground'>
                            Classify this output, check it, route on it
                          </span>
                        </div>
                      </ContextMenuItem>
                    )}
                    {repeatPattern.available && (
                      <ContextMenuItem
                        onClick={isLocked ? undefined : handleMakeRepeat}
                        disabled={isLocked}
                        data-testid='pattern-make-repeat'
                        className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                      >
                        <RotateCcw className='w-4 h-4 text-foreground' />
                        <div className='flex flex-col'>
                          <span className='text-sm'>
                            Make this part repeat until it passes
                          </span>
                          <span className='text-xs text-muted-foreground'>
                            Adds a check and a Repeat Until, wired up
                          </span>
                        </div>
                      </ContextMenuItem>
                    )}
                    <ContextMenuSeparator />
                  </>
                )}
              {/* --- end Phase 5 --- */}
              {showStandardNodeActions && (
                <ContextMenuItem
                  onClick={isLocked ? undefined : handleCopy}
                  disabled={isLocked}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                >
                  <Copy className='w-4 h-4 text-foreground' />
                  <span className='text-sm'>
                    {`Copy (${selectedCountAtOpen} node${selectedCountAtOpen > 1 ? 's' : ''})`}
                  </span>
                </ContextMenuItem>
              )}
              {showStandardNodeActions && canGroupAtOpen && onGroupNodes && (
                <ContextMenuItem
                  onClick={isLocked ? undefined : handleGroup}
                  disabled={isLocked}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                >
                  <Group className='w-4 h-4 text-foreground' />
                  <span className='text-sm'>
                    {`Group nodes (${groupEligibleCountAtOpen})`}
                  </span>
                </ContextMenuItem>
              )}
              {showUngroupAction && (
                <ContextMenuItem
                  onClick={isLocked ? undefined : handleUngroup}
                  disabled={isLocked}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                >
                  <Ungroup className='w-4 h-4 text-foreground' />
                  <span className='text-sm'>Ungroup nodes</span>
                </ContextMenuItem>
              )}
              {showStandardNodeActions && (
                <ContextMenuItem
                  onClick={isLocked ? undefined : handleDuplicate}
                  disabled={isLocked}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                >
                  <CopyPlus className='w-4 h-4 text-foreground' />
                  <span className='text-sm'>
                    {`Duplicate (${selectedCountAtOpen} node${selectedCountAtOpen > 1 ? 's' : ''})`}
                  </span>
                </ContextMenuItem>
              )}
              {showStandardNodeActions && (
                <ContextMenuItem
                  onClick={isLocked ? undefined : handleDelete}
                  disabled={isLocked}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-50 dark:focus:bg-red-950'
                >
                  <Trash2 className='w-4 h-4 text-red-500' />
                  <span className='text-sm'>
                    {`Delete (${selectedCountAtOpen} node${selectedCountAtOpen > 1 ? 's' : ''})`}
                  </span>
                </ContextMenuItem>
              )}
            </>
          )}

          {/* Paste Option */}
          {clipboardNodeCount > 0 && (
            <>
              <ContextMenuItem
                onClick={isLocked ? undefined : handlePaste}
                disabled={isLocked}
                {...(!hasSelectedNodes ? keepInputFocusProps : {})}
                className='flex items-center gap-3 px-3 py-2 cursor-pointer'
              >
                <Clipboard className='w-4 h-4 text-blue-500' />
                <span className='text-sm'>
                  {`Paste (${clipboardNodeCount} node${clipboardNodeCount > 1 ? 's' : ''})`}
                </span>
              </ContextMenuItem>
            </>
          )}

          {!hasSelectedNodes && clipboardNodeCount > 0 && (
            <ContextMenuSeparator />
          )}

          {/* Patterns - one-click multi-node stamps (only when every node in
              the pattern exists in this org's catalog) */}
          {!hasSelectedNodes && !searchQuery && judgmentGateStructs && (
            <>
              <div className='px-3 py-2'>
                <p className='text-sm font-medium text-muted-foreground'>
                  Patterns
                </p>
              </div>
              <ContextMenuItem
                onClick={isLocked ? undefined : handleStampJudgmentGate}
                disabled={isLocked}
                {...keepInputFocusProps}
                className='flex items-center gap-3 px-3 py-2 cursor-pointer'
              >
                <GitBranch className='w-4 h-4 text-foreground' />
                <div className='flex flex-col'>
                  <span className='text-sm'>Judgment gate</span>
                  <span className='text-xs text-muted-foreground'>
                    Classify → Validate → Switch, pre-wired
                  </span>
                </div>
              </ContextMenuItem>
              {/* --- Phase 6: insertable examples --- */}
              <ContextMenuSub>
                <ContextMenuSubTrigger
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer'
                  data-testid='pattern-examples'
                >
                  <Sparkles className='w-4 h-4 text-foreground' />
                  <div className='flex flex-col'>
                    <span className='text-sm'>Examples</span>
                    <span className='text-xs text-muted-foreground'>
                      Small, working logic-node graphs to start from
                    </span>
                  </div>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className='w-72'>
                  {LOGIC_TEMPLATES.map((template) => {
                    const availability = templateAvailability(
                      template,
                      groupedNodes,
                    );
                    return (
                      <ContextMenuItem
                        key={template.id}
                        disabled={isLocked || !availability.available}
                        data-testid={`pattern-example-${template.id}`}
                        className='flex flex-col items-start gap-0 px-3 py-2 cursor-pointer'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsertTemplate(template.id);
                        }}
                      >
                        <span className='text-sm'>{template.title}</span>
                        <span className='text-xs text-muted-foreground'>
                          {template.summary}
                        </span>
                      </ContextMenuItem>
                    );
                  })}
                </ContextMenuSubContent>
              </ContextMenuSub>
              {/* --- end Phase 6 --- */}
              <ContextMenuSeparator />
            </>
          )}

          {/* Recently Used and Suggestions Section - only show when not searching */}
          {!hasSelectedNodes &&
            !searchQuery &&
            (recentNodes.length > 0 ||
              suggestedNodes.length > 0 ||
              Object.keys(groupedNodes).length === 0) && (
              <>
                <div className='px-3 py-2'>
                  <p className='text-sm font-medium text-muted-foreground'>
                    {recentNodes.length > 0 ? 'Recently used' : 'Suggestions'}
                  </p>
                </div>
                {Object.keys(groupedNodes).length === 0 ? (
                  <div className='px-3 py-4 text-center'>
                    <p className='text-sm text-muted-foreground'>
                      No suggestions available
                    </p>
                  </div>
                ) : (
                  <div className='space-y-1 px-2 py-1'>
                    {/* Show recent nodes first */}
                    {recentNodes.map((node, index) => {
                      const actionIdx = index;
                      return (
                        <ContextMenuItem
                          key={`recent-${index}`}
                          className='p-0 block focus:bg-transparent'
                          onClick={() => handleNodeClick(node.nodeStruct)}
                          data-action-index={actionIdx}
                          {...keepInputFocusProps}
                        >
                          <NodeItem
                            node={node.nodeStruct}
                            showGripIcon={false}
                            isLocked={isLocked}
                            isHighlighted={highlightedIndex === actionIdx}
                          />
                        </ContextMenuItem>
                      );
                    })}

                    {/* Fill remaining slots with suggested nodes */}
                    {suggestedNodes.map((node, index) => {
                      const actionIdx = recentNodes.length + index;
                      return (
                        <ContextMenuItem
                          key={`suggested-${index}`}
                          className='p-0 block focus:bg-transparent'
                          onClick={() => handleNodeClick(node.nodeStruct)}
                          data-action-index={actionIdx}
                          {...keepInputFocusProps}
                        >
                          <NodeItem
                            node={node.nodeStruct}
                            showGripIcon={false}
                            isLocked={isLocked}
                            isHighlighted={highlightedIndex === actionIdx}
                          />
                        </ContextMenuItem>
                      );
                    })}
                  </div>
                )}
              </>
            )}

          {/* Search Results Items */}
          {searchQuery && (
            <div className='space-y-1 px-2 py-1'>
              {filteredNodes.slice(0, 10).map((node, index) => {
                return (
                  <ContextMenuItem
                    key={`search-${index}`}
                    className='p-0 block focus:bg-transparent'
                    onClick={() => handleNodeClick(node.nodeStruct)}
                    data-action-index={index}
                    {...keepInputFocusProps}
                  >
                    <NodeItem
                      node={node.nodeStruct}
                      showGripIcon={false}
                      isLocked={isLocked}
                      isHighlighted={highlightedIndex === index}
                    />
                  </ContextMenuItem>
                );
              })}
            </div>
          )}
        </div>
      </ContextMenuContent>
    </ContextMenu>
  );
};
